import type { PlanAttachmentOut, PlanNodeOut, PlanOccurrenceOut, PlanStatus } from '@/client';
import { MaintenanceActivityKind, type MaintenanceActivityEvent } from '@/components/maintenance/model/cardViewModels';
import { PlanStatuses } from '@/components/maintenance/model/planVocabulary';
import { isWarrantyNode } from '@/components/maintenance/model/planSelectors';
import { isFixedOnSchedule } from '@/components/maintenance/model/planReviewFilter';
import { NO_VISIBLE_DOCUMENTS, planSourcesForCard } from '@/components/maintenance/model/planNodeFacts';
import { formatDate } from '@/utils/dateUtils';

/**
 * How a card that is already settled reads in the History timeline.
 *
 * History and Upcoming are the same nodes from the same endpoint, told apart by `status` alone —
 * there is no separate "completed task" in v2. So a row asks this what outcome it is showing, and
 * gets `null` for anything still ahead, which is what keeps the treatment off the forward-looking
 * list.
 *
 * These also describe a settled card while its row exits the forward-looking list. The occurrence
 * query uses the concluded subset below; dismissed cards leave the response before they can carry
 * an occurrence row.
 */
export const HistoryOutcomes = {
	/** Done, and credited. The plan has rolled on to the next occurrence. */
	Completed: 'completed',
	/** Deliberately not done. Advances the series without crediting it. */
	Skipped: 'skipped',
	/** Discarded by the household rather than acted on, and sent to the Archive. */
	Dismissed: 'dismissed',
	/** The window closed. A warranty whose cover ended, a one-off whose date passed. */
	Expired: 'expired',
} as const;

export type HistoryOutcome = (typeof HistoryOutcomes)[keyof typeof HistoryOutcomes];

const OUTCOME_BY_STATUS: Partial<Record<NonNullable<PlanNodeOut['status']>, HistoryOutcome>> = {
	[PlanStatuses.Completed]: HistoryOutcomes.Completed,
	[PlanStatuses.Skipped]: HistoryOutcomes.Skipped,
	[PlanStatuses.Dismissed]: HistoryOutcomes.Dismissed,
	[PlanStatuses.Expired]: HistoryOutcomes.Expired,
};

/** The outcome this card settled on, or `null` while it is still something to do. */
export const historyOutcome = (node: PlanNodeOut): HistoryOutcome | null =>
	(node.status && OUTCOME_BY_STATUS[node.status]) ?? null;

/** What an outcome is called on screen. */
export const outcomeLabel = (outcome: HistoryOutcome): string => {
	switch (outcome) {
		case HistoryOutcomes.Completed:
			return 'Completed';
		case HistoryOutcomes.Skipped:
			return 'Skipped';
		case HistoryOutcomes.Dismissed:
			return 'Discarded';
		case HistoryOutcomes.Expired:
			// A cover-review task that lapsed genuinely was not reviewed. A warranty whose cover ran
			// out is a different thing, and `outcomeExpiredLabel` tells them apart.
			return 'Not reviewed';
	}
};

/**
 * The one line under the title, in the household's own words where they left any.
 *
 * A note says more than a status does, so it takes the status's place when present. This one reads
 * the card's current occurrence; a History row carries its own note and uses `historyRowSubtitle`.
 */
export const historySubtitle = (node: PlanNodeOut, outcome: HistoryOutcome): string =>
	node.occurrence?.note?.trim() ||
	(outcome === HistoryOutcomes.Expired ? expiredLabel(isWarrantyNode(node)) : outcomeLabel(outcome));

/**
 * What an ended thing is called, which depends on what ended.
 *
 * `expired` covers two cases the plan does not distinguish: a warranty whose cover ran out, and a
 * task that lapsed unreviewed. Calling a finished warranty "Not reviewed" says the household failed
 * to do something, when what happened is that their cover ended. Every expired card in the captured
 * properties is a warranty.
 */
export const expiredLabel = (isWarranty: boolean): string => (isWarranty ? 'Cover ended' : 'Not reviewed');

/**
 * The same line for a History row, which carries its own note rather than the card's.
 *
 * A row for a date the household wrote about says what they wrote. Everything else falls back to
 * the outcome, which is all a bare date can tell you.
 */
export const historyRowSubtitle = (row: HistoryRow): string => {
	if (row.note) return row.note;
	if (row.outcome === HistoryOutcomes.Expired) return expiredLabel(row.isWarranty);
	return outcomeLabel(row.outcome);
};

/**
 * The occurrence's scheduled date when it differs from the day History files the event under.
 *
 * Work that was acted on belongs under the day it happened, while its due date remains useful
 * context. An expiry was nobody's action and carries no such day, so that row is already filed
 * under this date and has nothing to set beside it.
 */
export const historyRowScheduledFor = (row: HistoryRow): string | null =>
	row.targetDate !== row.date ? row.targetDate : null;

/**
 * An occurrence's state, in the words the History tab uses.
 *
 * Membership of this table is what decides whether an occurrence is history at all, and the date
 * plays no part in it. Completing a task dated two years out is still something that happened, and
 * belongs in the record on the day it was done. Only `upcoming` and `due_soon` are absent, because
 * nothing has happened to them yet.
 *
 * **`overdue` is deliberately absent, and that is what removes it from the request too.**
 * `HISTORY_OCCURRENCE_STATUSES` is derived from this table, so a status that draws nothing is never
 * asked for. It is the one state the API reports that is not an outcome at all: the backend defines
 * it as "next_due has passed — work you are late on", which is live and still actionable — an
 * overdue occurrence carries a `resolve_token`, so acting on that date today still credits it. Every
 * member here has settled. Reporting it as a past verdict told households they had failed work that
 * was merely late, and on the five captured properties 310 of its 314 occurrences were dated before
 * their own card even reached the plan, since the series is materialised backwards from its anchor.
 *
 * "Behind on this" is the card's own live state, which `BEHIND_STATUSES` in `planVocabulary` owns.
 *
 * `dismissed` is absent too, but for a reason that needs no decision here: a dismissed obligation is
 * dropped from the response before its occurrences are built, so none ever reaches this table. It
 * stays a card-level outcome in `OUTCOME_BY_STATUS`. `overdue` is the only status that arrives on
 * every read and is passed over on purpose.
 */
const OUTCOME_BY_OCCURRENCE_STATUS: Partial<Record<NonNullable<PlanStatus>, HistoryOutcome>> = {
	[PlanStatuses.Completed]: HistoryOutcomes.Completed,
	[PlanStatuses.Skipped]: HistoryOutcomes.Skipped,
	[PlanStatuses.Expired]: HistoryOutcomes.Expired,
};

/**
 * The occurrence statuses the history read asks for, taken from the map above so the request and
 * the render cannot disagree.
 *
 * `snoozed` is not among them. History is concluded work, and a snooze concludes nothing: it moves
 * one occasion to a later day, the way a reschedule moves the series, and neither is a row here.
 * Both are entries in the card's own Activity, and the moved occasion is on the schedule at its
 * new date.
 *
 * An occurrence with any other status draws no row. `upcoming` and `due_soon` are the plan ahead,
 * not a record of anything, so asking for them costs a tenfold larger response that is then mostly
 * discarded. Adding an outcome to the map widens the request with it.
 */
export const HISTORY_OCCURRENCE_STATUSES = Object.keys(OUTCOME_BY_OCCURRENCE_STATUS) as PlanStatus[];

/** One past occurrence, flattened out of the tree and ready to draw. */
export interface HistoryRow {
	/** Opens the card's drawer. An occurrence has no page of its own. */
	nodeId: string;
	title: string;
	/** The day History files the event under: the day it was acted on where known, else target date. */
	date: string;
	/** The occurrence's scheduled date, retained as its address and as context for early/late work. */
	targetDate: string;
	/**
	 * The period this instance belongs to — `2026-09`, or `2026` on an annual rule.
	 *
	 * The only thing a decision in the log and an occurrence in the plan have in common. The log
	 * names the instance an action settled by its bucket and carries no date; the plan carries the
	 * date and the bucket both. Joining on it is what lets a completion say what it scheduled next.
	 */
	bucket: string | null;
	outcome: HistoryOutcome;
	/**
	 * This date is a group's roll-up of its members, so its outcome is whichever member landed first.
	 * A row reading "completed" may be covering members that were not.
	 *
	 * TODO(schedule/group-history-members): Have the plan occurrence retain each contributing child's
	 * node id and outcome (or stop clearing the child occurrence arrays), then show those child names
	 * and outcomes beneath the parent event. The current child list alone cannot say which children
	 * participated on this date and must not be presented as though they all did.
	 */
	isGroupRollup: boolean;
	systemName?: string | null;
	/**
	 * The day the work was answered, which is often not the day it was due.
	 *
	 * Completions and skips both carry one — `completed_at` and `skipped_at` are the same idea for
	 * the two outcomes a household records. Null on date-derived outcomes such as an expiry, and on
	 * rows written before the endpoint stored these, which stay filed under
	 * `targetDate` rather than claiming an event date the occurrence does not supply.
	 */
	actedOn: string | null;
	/** What the household wrote about this occasion, in their own words. */
	note: string | null;
	/** Files filed against this occasion — a receipt, a photo — rather than against the card. */
	attachmentCount: number;
	/**
	 * The files themselves, which the count alone could not reach.
	 *
	 * `attachment_id` is the document id stringified, so a row resolves to a record through the same
	 * lookup the Source tab uses. Kept alongside the count rather than replacing it: the count is
	 * what a dense row draws, and it survives a read that sends no records.
	 */
	attachments: PlanAttachmentOut[];
	/** Whether the card marks cover rather than work, which changes what "expired" is called. */
	isWarranty: boolean;
	/**
	 * The token that acts on this occurrence rather than on the card's current one.
	 *
	 * A card names only the occurrence it is showing, so undoing a completion recorded against March
	 * is impossible from the card once the plan has moved to April. This is what makes the row itself
	 * actionable. Null on an occurrence the read did not mint one for.
	 */
	resolveToken: string | null;
	/**
	 * Who acted, as a `user_id`. Null when the record does not say.
	 *
	 * A number rather than a name, so drawing it needs the house's members. Null is not the same as
	 * absent: absent would mean Bundle acted, and crediting the app for a household's work is the one
	 * reading that must never happen.
	 */
	actor: number | null;
}

/**
 * The record row known the moment a close-out write succeeds, before the history read rematerialises.
 *
 * Its occurrence identity is the same one the server row will use, so React updates this row in
 * place when richer note, attachment, actor and resolve data arrive instead of inserting a card.
 */
export const optimisticHistoryRow = (
	node: PlanNodeOut,
	action: 'complete' | 'bulk_complete' | 'skip',
	actedOn: string,
): HistoryRow | null => {
	const targetDate = node.action_target?.target_date ?? node.next_due;
	if (!targetDate) return null;
	const completed = action !== 'skip';
	return {
		nodeId: node.node_id,
		title: node.title,
		date: actedOn,
		targetDate,
		bucket: node.action_target?.occurrence_bucket ?? null,
		outcome: completed ? HistoryOutcomes.Completed : HistoryOutcomes.Skipped,
		isGroupRollup: node.node_type === 'group',
		systemName: node.system?.display_name ?? null,
		actedOn,
		note: null,
		attachmentCount: 0,
		attachments: [],
		isWarranty: false,
		resolveToken: null,
		actor: null,
	};
};

/** Adds locally accepted rows without duplicating the richer server row once it materialises. */
export const withOptimisticHistoryRows = (
	rows: readonly HistoryRow[],
	optimisticRows: readonly HistoryRow[],
): HistoryRow[] => {
	const serverKeys = new Set(rows.map((row) => occurrenceKey(row.nodeId, row.targetDate)));
	return [
		...rows,
		...optimisticRows.filter((row) => !serverKeys.has(occurrenceKey(row.nodeId, row.targetDate))),
	].sort((left, right) => right.date.localeCompare(left.date));
};

/**
 * Everything that has happened across the plan, newest first.
 *
 * What makes a row is the occurrence's state, never its date. A task completed today counts even
 * though the date it credits is years out — the household did the work, and a record that hid it
 * until its due date came round would be a record of the calendar rather than of what was done.
 * `upcoming` and `due_soon` are the only states left out, being the ones nothing has happened to.
 *
 * Walks the whole tree and concatenates without deduplicating, which is safe by construction: a
 * group rolls its members' occurrences up by date and then clears the children's arrays, so a child
 * of a group always has an empty one. Deduplicating would instead lose two different cards that
 * genuinely share a date.
 *
 * Occurrences only arrive when the read asked for a window, so this is empty on a plain list read no
 * matter what the cards say.
 */
/**
 * The day an outcome was performed, read from the one field that outcome owns.
 *
 * Selected by outcome rather than by coalescing the two dates: each belongs to exactly one status,
 * and the endpoint clears whichever no longer applies. Null on date-derived outcomes such as an
 * expiry, and on a skip recorded before `skipped_at` existed, which then stays filed under the date
 * it was due.
 */
const actedOnOf = (occurrence: PlanOccurrenceOut, outcome: HistoryOutcome): string | null => {
	if (outcome === HistoryOutcomes.Completed) return occurrence.completed_at ?? null;
	if (outcome === HistoryOutcomes.Skipped) return occurrence.skipped_at ?? null;
	return null;
};

export const historyRows = (nodes: readonly PlanNodeOut[]): HistoryRow[] => {
	const rows: HistoryRow[] = [];

	const walk = (node: PlanNodeOut) => {
		for (const occurrence of node.occurrence?.occurrences ?? []) {
			const targetDate = occurrence.target_date;
			if (!targetDate) continue;

			const outcome = occurrence.status ? OUTCOME_BY_OCCURRENCE_STATUS[occurrence.status] : undefined;
			if (!outcome) continue;
			const actedOn = actedOnOf(occurrence, outcome);
			const date = actedOn ?? targetDate;

			rows.push({
				nodeId: node.node_id,
				title: node.title,
				date,
				targetDate,
				bucket: occurrence.occurrence_bucket ?? null,
				outcome,
				isGroupRollup: node.node_type === 'group',
				systemName: node.system?.display_name ?? null,
				actedOn,
				note: occurrence.note?.trim() || null,
				attachmentCount: occurrence.attachment_count ?? 0,
				attachments: occurrence.attachments ?? [],
				isWarranty: isWarrantyNode(node),
				actor: occurrence.actor ?? null,
				resolveToken: occurrence.resolve_token ?? null,
			});
		}

		node.children?.forEach(walk);
	};

	nodes.forEach(walk);

	// Completed work is ordered by when it happened. Outcomes without an acted-on timestamp retain
	// their target date, which is the only honest date the occurrence supplies for them.
	return rows.sort((left, right) => right.date.localeCompare(left.date));
};

/** Everything that happened on one date, which is the unit the record draws a gutter for. */
export interface HistoryDay {
	date: string;
	rows: HistoryRow[];
}

/** The rows grouped under the day their event happened, newest day first. */
export const historyRowsByDate = (rows: readonly HistoryRow[]): HistoryDay[] => {
	const days = new Map<string, HistoryRow[]>();
	for (const row of rows) days.set(row.date, [...(days.get(row.date) ?? []), row]);

	return [...days.entries()].map(([date, dayRows]) => ({ date, rows: dayRows }));
};

export interface HistoryMonth {
	/** The first day of the month, as the heading needs a date to format. */
	iso: string;
	/** How many rows the month holds, across all of its days. */
	rowCount: number;
	days: HistoryDay[];
}

/**
 * The same rows, gathered under the month they happened in, newest month first.
 *
 * A record is read by scanning, and a long column of bare day numbers gives a reader nothing to hold
 * on to — 28, 15, 14, 12, 5 says far less than the same numbers under "June". Upcoming has grouped
 * this way all along; History was a flat list.
 *
 * Day order within a month is left as `historyRowsByDate` produced it, so whatever decides the
 * record's order decides it in one place rather than two.
 */
export const historyRowsByMonth = (rows: readonly HistoryRow[]): HistoryMonth[] => {
	const months = new Map<string, { date: string; rows: HistoryRow[] }[]>();
	for (const day of historyRowsByDate(rows)) {
		const month = day.date.slice(0, 7);
		months.set(month, [...(months.get(month) ?? []), day]);
	}

	return [...months.entries()].map(([month, days]) => ({
		iso: `${month}-01`,
		rowCount: days.reduce((count, day) => count + day.rows.length, 0),
		days,
	}));
};

const ACTIVITY_KIND_BY_OUTCOME: Record<HistoryOutcome, MaintenanceActivityKind> = {
	[HistoryOutcomes.Completed]: MaintenanceActivityKind.Completed,
	[HistoryOutcomes.Skipped]: MaintenanceActivityKind.Skipped,
	[HistoryOutcomes.Expired]: MaintenanceActivityKind.Expired,
	// Never reached: a dismissed obligation leaves the response before its occurrences are built.
	[HistoryOutcomes.Dismissed]: MaintenanceActivityKind.Skipped,
};

/**
 * A card's occurrences as entries for the drawer's activity timeline.
 *
 * `resolveActor` turns the occurrence's `user_id` into a name. An id it cannot place falls back to
 * the `unknown` shape and never to an absent actor: absent means Bundle acted, and a completion the
 * household recorded must not read as the app's work. So an unplaceable id says the name is not
 * available, which is true, rather than crediting the wrong party.
 *
 * `date` has already resolved the event day: the day the work was completed or skipped where the
 * occurrence records one, otherwise its target date. The occurrence link separately retains the
 * target date as identity.
 */
export const historyActivityEvents = (
	rows: readonly HistoryRow[],
	resolveActor: (userId: number | null) => string | null = () => null,
): MaintenanceActivityEvent[] =>
	rows.map((row) => {
		const name = resolveActor(row.actor);

		return {
			kind: ACTIVITY_KIND_BY_OUTCOME[row.outcome],
			at: row.date,
			actor: row.actor != null && name ? { kind: 'user', userId: row.actor, name } : { kind: 'unknown' },
			link: { kind: 'occurrence', nodeId: row.nodeId, date: row.targetDate },
			evidence: row.attachments,
		};
	});

/**
 * The date each instance falls on, keyed by the bucket that names it.
 *
 * The record and the log describe the same occasions from two sides and agree on nothing but the
 * bucket: a row is addressed as `nodeId@date`, while a decision carries a period and no date at all.
 * This is the join that lets an entry built from the log know which row it is — without it, an entry
 * cannot tell that it is the one the URL is pointing at.
 */
export const datesByBucket = (rows: readonly HistoryRow[]): Map<string, string> =>
	new Map(rows.flatMap((row) => (row.bucket ? [[row.bucket, row.targetDate] as const] : [])));

/**
 * Where the series went after each instance, keyed by the bucket that instance belongs to.
 *
 * A completion's whole consequence is the date it pushed the work to, and nothing in the decision
 * log says it: the log records that an instance was settled, not what settling it produced. The plan
 * knows, because the instance after this one carries its own `target_date` — so the answer is the
 * *next occurrence in the record*, and for the most recent instance it is the card's `next_due`.
 *
 * **That is one step short of what a completion produced**, and the two part company exactly when a
 * reschedule lands between them: the following occurrence then carries the rescheduled date rather
 * than the one the completion set. The plan carries nothing that would tell them apart, so the
 * reading here is "what came next", which is true, rather than "what this caused", which is not
 * always.
 *
 * Occurrences with no bucket are skipped — they cannot be addressed by a log action, so nothing
 * would ever look them up.
 */
export const scheduleAfterEachInstance = (
	rows: readonly HistoryRow[],
	nextDue: string | null | undefined,
): Map<string, string> => {
	// Oldest first, which is the order "the one after this" is a question about. `historyRows` hands
	// them back newest first, for the list.
	const ordered = [...rows].sort((left, right) => left.targetDate.localeCompare(right.targetDate));
	const latest = ordered.at(-1);
	// A one-off keeps reporting its own settled date as `next_due`, there being no occurrence after
	// it. Read as "what came next" that says the completion scheduled the work for the day it was
	// just completed on — a date the card already has an entry for, stated again as its consequence.
	const followsTheLast = nextDue && latest && nextDue !== latest.targetDate ? nextDue : null;
	const followingDates = [...ordered.slice(1).map((row) => row.targetDate), followsTheLast];

	return new Map(
		ordered.flatMap((row, index) => {
			const following = followingDates[index];
			return row.bucket && following ? [[row.bucket, following] as const] : [];
		}),
	);
};

/**
 * The date the card was first set for, as closely as the plan can say it.
 *
 * The plan records no original schedule — only `added_at` and the occurrences that exist now — so
 * this is the earliest occurrence in the record, which is the nearest true thing available.
 *
 * **Bounded below by `added_at`,** which is not a detail. The endpoint materialises a recurring
 * series backwards from its anchor as well as forwards, so a card added in 2026 can carry invented
 * occurrences from 2023 (see `OUTCOME_BY_OCCURRENCE_STATUS`). Taking the earliest of those would
 * have a card announce it was first scheduled three years before it existed.
 *
 * Null when the record holds nothing at or after the add, which is the ordinary state of a card
 * whose first date is still ahead of it: the occurrences that would answer this are `upcoming`, and
 * the record does not ask for those. Saying nothing is right there — `next_due` is a tempting
 * substitute and is the very thing that made this entry wrong before.
 */
export const firstScheduledDate = (rows: readonly HistoryRow[], addedAt: string | null | undefined): string | null => {
	if (!addedAt) return null;

	const onOrAfterAdd = rows.map((row) => row.targetDate).filter((date) => date >= addedAt);
	return onOrAfterAdd.length ? onOrAfterAdd.reduce((earliest, date) => (date < earliest ? date : earliest)) : null;
};

/**
 * The card's own beginning — when it joined the plan, and what it was read out of.
 *
 * `added_at` is the date the card first entered the plan, and it survives a reconcile, so it is the
 * card's birthday rather than the last time the pipeline ran. `source_origin` says who put it there:
 * anything not `user` came from Bundle reading a document.
 *
 * Without this a task nobody has touched has an empty history — which on a newly created property is
 * every task on it, and the first thing a household opens.
 *
 * Returns nothing when the card carries no `added_at`. Children of a group have none today, so a
 * sub-task's account still starts at its first occurrence.
 *
 * Newest first, like the rest of the timeline, which puts the add itself last — it is the oldest
 * thing that can have happened to a card, so nothing may sit beneath it.
 *
 * It no longer says what the card was scheduled for. It used to, reading `next_due` — but that is
 * where the series stands *now*, not what it was set to on the day it arrived, so a card three
 * completions in claimed to have been born scheduled for a date it only reached later. What the card
 * is set to today is {@link cardScheduleEvent}, and the plan carries no record of the first date.
 */
export const cardAddedEvents = (
	node: PlanNodeOut,
	options: {
		resolveDocumentPublicId?: (documentId: number) => string | undefined;
		/**
		 * Document ids returned by the visibility-filtered Records list.
		 *
		 * TODO(schedule-source-visibility): make this required. Omitted, the timeline names no source
		 * at all rather than naming one the reader may not be entitled to — see
		 * docs/schedule/source-visibility.md.
		 */
		visibleDocumentIds?: ReadonlySet<number>;
		/** The date the card started on — {@link firstScheduledDate}, which the caller has the rows for. */
		firstScheduledFor?: string | null;
	} = {},
): MaintenanceActivityEvent[] => {
	// TODO(schedule/custom-entry-added-at): `added_at` is null on a freshly created custom entry, so a
	// household's own task draws no "Task added" entry at all — its creation is absent from the
	// account, and with it the `scheduledFor` that makes the card's date read as the day it was set
	// for rather than as where it still stands. Populate it from the entry's own creation, alongside
	// the creator id the TODO below wants; the client has no other durable record of either.
	//
	// Cover is the exception: it still came from a document, so the entry carries the source even
	// with no date to show. `at: ''` sorts oldest under the descending compare, which is where an add
	// belongs, and `ActivityLine` omits a date it is not given.
	// TODO(api-warranty-added-at): populate `added_at` on warranty and compliance cards.
	if (!node.added_at && !isFixedOnSchedule(node)) return [];

	const sources = planSourcesForCard(
		node,
		options.visibleDocumentIds ?? NO_VISIBLE_DOCUMENTS,
		options.resolveDocumentPublicId,
	);

	return [
		{
			kind: MaintenanceActivityKind.Added,
			origin: true,
			at: node.added_at ?? '',
			// An absent actor reads as Bundle, which is exactly right for a card it derived from a
			// document. A household-authored card must remain unattributed until the plan identifies which
			// member created it: `source_origin: user` says only that it was a person, and shared properties
			// make assuming the signed-in user incorrect.
			// TODO(schedule/custom-entry-author): Expose the custom entry's creator user id on `PlanNodeOut`,
			// then resolve it through `useHouseMemberNames` so the current user reads as "You" and another
			// household member reads by name.
			actor: node.source_origin === 'user' ? { kind: 'unknown' } : undefined,
			// The document the card was read out of, where there is one. A `system` card — a checklist
			// Bundle schedules for every home — was read out of nothing and carries none.
			...(sources.length ? { sources } : {}),
			...(options.firstScheduledFor ? { scheduledFor: options.firstScheduledFor } : {}),
		},
	];
};

/**
 * Where the card stands today: the date it is currently set for.
 *
 * **Not an event, and deliberately not dated.** Every other entry says a thing happened on a day.
 * This one says a thing is *going to* happen, which is true of no past day at all — so it cannot be
 * sorted among them. Dating it by `next_due` would put a card due in 2031 above completions that
 * really took place, and an overdue card below them; dating it by `added_at` is what used to fold it
 * into the add, where it read as a claim about the past that was false.
 *
 * So it is pinned to the top of the timeline by whoever assembles it, and drawn without an author or
 * a date — nobody did it, and it has not happened yet.
 *
 * Absent on a card with no date, which is an ordinary state: an awareness obligation has none by
 * design, and a provisional card may have no anchor yet.
 */
export const cardScheduleEvent = (node: PlanNodeOut): MaintenanceActivityEvent | null =>
	node.next_due ? scheduleEntry(node.next_due) : null;

/**
 * One statement that the plan stands at a date, wherever it came from.
 *
 * Shared by the card's current date and by every date a settled instance sent the work to, so the
 * two cannot drift into reading differently — they are the same claim about the same kind of thing,
 * and on the newest instance they are literally the same date.
 *
 * `at` is the day it points at. Never sorted on, since these are placed rather than ordered, and
 * never drawn — a schedule happened on no day. It is the only honest date to carry, it is what
 * `activityEntryKey` needs, and it is what tells two adjacent statements apart.
 */
const scheduleEntry = (date: string, liftedFrom?: string): MaintenanceActivityEvent => ({
	kind: MaintenanceActivityKind.Scheduled,
	at: date,
	detail: `for ${formatDate(date)}`,
	...(liftedFrom ? { liftedFrom } : {}),
});

/**
 * Where the card's own date belongs in its account.
 *
 * Pinned above everything while it is genuinely ahead: a date nothing has settled is the card's
 * standing state rather than an event, which is why it carries no author and no day of its own.
 *
 * A date an occurrence has already settled is not standing at all. A one-off completed on the day
 * it was due still reports that day as `next_due`, and pinning it at the top then says the card was
 * scheduled after it was completed. It goes immediately below the entry that settled it, where it
 * reads as the schedule that entry answered — which is what it is.
 */
export const withStandingSchedule = (
	standing: MaintenanceActivityEvent | null,
	events: readonly MaintenanceActivityEvent[],
	settledDates: ReadonlySet<string>,
): MaintenanceActivityEvent[] => {
	if (!standing) return [...events];
	if (!settledDates.has(standing.at)) return [standing, ...events];

	// Below the entry that settled this date. Both candidates require an entry that ends an occasion:
	// a note or an attachment filed against the same occurrence addresses it without having settled
	// it, and being newer would otherwise win. Preferably the settling entry that names this occasion
	// — but an entry only carries that address when its action was bucketed and a row shares the
	// bucket, which a one-off need not be — and failing that, the newest settling entry of any date.
	const settles = (event: MaintenanceActivityEvent) => SETTLING_ACTIVITY_KINDS.has(event.kind);
	const addressed = events.findIndex(
		(event) => settles(event) && event.link?.kind === 'occurrence' && event.link.date === standing.at,
	);
	const settling = events.findIndex(settles);
	const after = addressed >= 0 ? addressed : settling;
	if (after < 0) return [standing, ...events];

	return [...events.slice(0, after + 1), standing, ...events.slice(after + 1)];
};

/** The entries that end an occasion, as against the ones that merely happened after one. */
const SETTLING_ACTIVITY_KINDS = new Set<MaintenanceActivityEvent['kind']>([
	MaintenanceActivityKind.Completed,
	MaintenanceActivityKind.Skipped,
	MaintenanceActivityKind.Expired,
]);

/** The occurrence dates nothing can still be scheduled for, because something ended them. */
export const settledOccurrenceDates = (rows: readonly HistoryRow[]): Set<string> =>
	new Set(rows.filter((row) => SETTLING_OUTCOMES.has(row.outcome)).map((row) => row.targetDate));

const SETTLING_OUTCOMES = new Set<HistoryOutcome>([
	HistoryOutcomes.Completed,
	HistoryOutcomes.Skipped,
	HistoryOutcomes.Expired,
]);

/**
 * Every schedule an entry produced, lifted out as an entry of its own directly above it.
 *
 * A card's account then alternates: what the household did, and where the plan stood afterwards.
 * Held beneath its producer instead, a date reads as a footnote to the completion rather than as
 * the next thing that was true of the card — and the newest one, which is where the card stands
 * today, is buried under an entry dated weeks earlier.
 *
 * Above rather than below, because the list runs newest first and a schedule is always later than
 * the thing that set it.
 *
 * **Consecutive schedules for the same day collapse.** The most recent settled instance sends the
 * work to exactly where the card now stands — `scheduleAfterEachInstance` reads `next_due` for it —
 * so the pinned standing entry and the one lifted out of that instance are the same fact stated
 * twice, adjacently. One of them goes.
 */
export const withScheduleEntries = (events: readonly MaintenanceActivityEvent[]): MaintenanceActivityEvent[] => {
	const expanded = events.flatMap((event) => {
		if (!event.scheduledFor) return [event];

		const { scheduledFor, ...producer } = event;
		return [scheduleEntry(scheduledFor, producer.at), producer];
	});

	return expanded.filter((event, index) => {
		const above = index > 0 ? expanded[index - 1] : undefined;
		const repeatsTheOneAbove =
			above?.kind === MaintenanceActivityKind.Scheduled &&
			event.kind === MaintenanceActivityKind.Scheduled &&
			above.at === event.at;

		return !repeatsTheOneAbove;
	});
};

/**
 * How one dated instance is named in the URL.
 *
 * An occurrence has no durable id of its own to address it by — `occurrence_id` is re-minted on every
 * materialization, and a group's rolled-up entry has none at all — so the card and the date are the
 * identity. The date here is the occurrence's target date, not the day it was completed. `@`
 * separates them because a node id is colon-delimited and never contains one.
 */
export const occurrenceKey = (nodeId: string, date: string): string => `${nodeId}@${date}`;

/** Whether this row is the one the URL is pointing at. */
export const isSelectedOccurrence = (row: HistoryRow, selected: string | undefined): boolean =>
	selected !== undefined && occurrenceKey(row.nodeId, row.targetDate) === selected;

/**
 * The date a key names, when it names an instance of *this* card. Null otherwise.
 *
 * The inverse of {@link occurrenceKey}, and the guard as well as the parser: it matches the card's
 * own id as a prefix rather than splitting on `@`, so a key another card left in the URL cannot make
 * this one read as though it were open at one of its own dates.
 */
export const occurrenceDateFor = (nodeId: string, key: string | undefined): string | null => {
	const prefix = `${nodeId}@`;
	return key?.startsWith(prefix) ? key.slice(prefix.length) : null;
};

/**
 * The same date, but only when it is a *past* occasion rather than the one the card is living on.
 *
 * A card's current occurrence is not history, however it was arrived at, and the distinction is not
 * the date's — it is whether `action_target` names it. An overdue card is the case that forces this:
 * its own live occurrence is materialised as `overdue`, so it draws a row on the History tab like
 * any other, and following that row would otherwise put the drawer into the past-occasion reading —
 * a "Missed" mark on a card that is due now, and no Complete offered on work that can still be done.
 *
 * One date per card per occurrence, so the dates are identity enough here and the check is made
 * before the record is read rather than after.
 */
export const pastOccurrenceDateFor = (node: PlanNodeOut, key: string | undefined): string | null => {
	const date = occurrenceDateFor(node.node_id, key);
	return date && date !== node.action_target?.target_date ? date : null;
};

/**
 * A stable identity for one timeline entry.
 *
 * Keyed on the occurrence it points at, never on `at`. A card's occurrences are dated by when the
 * household acted, and acting on six of them in one sitting gives all six the same `at` — so a date
 * cannot tell them apart, and React only warns about the duplicate rather than failing. `nodeId@date`
 * addresses the instance itself and stays unique however the entry chooses to date itself.
 */
export const activityEntryKey = (event: MaintenanceActivityEvent, generatedAt?: string): string => {
	const identity = event.link?.kind === 'occurrence' ? occurrenceKey(event.link.nodeId, event.link.date) : event.at;

	// `liftedFrom` is the only thing separating two schedule entries for one date, which the account
	// keeps on purpose — see `withScheduleEntries`. Without it React saw one key twice.
	return `${event.kind}-${identity}-${event.liftedFrom ?? ''}-${generatedAt ?? ''}`;
};
